import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  selectUserFacilities, 
  selectError, 
  selectPagination,
  setSelectedFacility 
} from '../../redux/slices/facilitiesSlice';
// Removed Redux auth selectors - now using useAuth() hook
import { useRole } from '../../hooks/useRole';
import { useFacilitySwitching } from '../../hooks/useFacilitySwitching';
import { 
  Box, 
  Typography, 
  Pagination, 
  CircularProgress, 
  TextField, 
  InputAdornment,
  Divider,
  Button,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import { useUserData } from '../../hooks/useUserData';

const FacilityFilter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasInitialFetch, setHasInitialFetch] = useState(false);
  const [defaultFacility, setDefaultFacility] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const dropdownRef = useRef(null);
  
  const userFacilities = useSelector(selectUserFacilities);
  // const loading = useAuth().loading; // Now using useAuth() hook
const error = useSelector(selectError);
  const { user, loading } = useUserData();
  // const user = useAuth().user; // Now using useAuth() hook
// const role = useAuth().user?.role; // Now using useAuth() hook
const pagination = useSelector(selectPagination);
  const { isAdminOrFacilityAdmin, isAdmin } = useRole();
  const { 
    switchDefaultFacility, 
    isDefaultFacility
  } = useFacilitySwitching();
  
  const fetchUserDefaultFacility = useCallback(async () => {
    if (!user?.uid) return;
    try {
      // Use Redux state instead of cache service
      if (user.defaultFacility) {
        // defaultFacility is now the complete facility object, extract the ID
        const facilityId = typeof user.defaultFacility === 'object' ? user.defaultFacility.id : user.defaultFacility;
        setDefaultFacility(facilityId);
      }
    } catch (error) {
      console.error('Error fetching user default facility:', error);
    }
  }, [user?.uid, user?.defaultFacility]);

  useEffect(() => {
    if (user?.uid && !hasInitialFetch) {
      // For admin users, fetch all facilities. For others, use pagination
      setHasInitialFetch(true);
      fetchUserDefaultFacility();
    }
  }, [dispatch, user, hasInitialFetch, fetchUserDefaultFacility, user?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSetDefaultFacility = async (facilityId) => {
    if (!user?.uid || defaultFacility === facilityId) return;
    setSaving(true);
    try {
      // Use the new hook function to switch facilities
      await switchDefaultFacility(facilityId);
      
      // Update local state
      setDefaultFacility(facilityId);
      
      // Update selected facility in Redux store for persistence
      const selectedFacilityObject = userFacilities.find(f => f.id === facilityId);
      if (selectedFacilityObject) {
        dispatch(setSelectedFacility(selectedFacilityObject));
        console.log('FacilityFilter: Updated selectedFacility in Redux:', selectedFacilityObject);
      }
      
      // Show success feedback to user
      console.log(`Default facility changed to: ${facilityId}`);
      
      // Find the facility name for the success message
      const facility = userFacilities.find(f => f.id === facilityId);
      const facilityName = facility ? facility.name : 'Unknown Facility';
      setSuccessMessage(`Default facility changed to ${facilityName}`);
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Error saving default facility:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFacilityNameClick = (facilityId, event) => {
    // Only allow admin and facility_admin to click facility names
    if (isAdminOrFacilityAdmin()) {
      navigate(`/facilities/${facilityId}/edit`);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // dispatch(fetchUserFacilities({ page: value, limit })); // Removed: now loaded centrally in AuthContext
  };

  const filteredFacilities = userFacilities.filter(facility =>
    facility.name && facility.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="facility-filter" ref={dropdownRef}>
      <button
        className="facility-filter-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
      >
        <BusinessIcon sx={{ mr: 1 }} />
        <span>Facility</span>
        {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </button>
      
      {error && (
        <div className="facility-error">
          Error loading facilities. Please try again.
        </div>
      )}
      
      {isOpen && (
        <div className="facility-dropdown">
          <div className="facility-dropdown-header">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Available Facilities
              </Typography>
            </Box>
            <TextField
              className="facility-search"
              size="small"
              placeholder="Search facilities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          
          <Divider />
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <div className="facility-empty facility-error">
              Failed to load facilities. Please refresh the page.
            </div>
          ) : filteredFacilities.length > 0 ? (
            <>
              <div className="facility-list">
                {filteredFacilities.map((facility) => {
                  const isDefault = isDefaultFacility(facility.id);
                  return (
                    <div key={facility.id}>
                      <div
                        className={`facility-row${isDefault ? ' default' : ''}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '4px 8px',
                          borderRadius: 6,
                          marginBottom: 2,
                          background: isDefault ? '#f5faff' : 'transparent',
                          boxShadow: isDefault ? '0 1px 4px rgba(33,150,243,0.08)' : 'none',
                          cursor: 'default',
                          border: isDefault ? '1px solid #2196f3' : '1px solid transparent',
                          transition: 'background 0.2s, box-shadow 0.2s',
                        }}
                      >
                        <Tooltip title={facility.address || ''} arrow placement="right">
                          <span 
                            className={isAdminOrFacilityAdmin() ? 'facility-name-clickable' : ''}
                            style={{ 
                              fontWeight: 500, 
                              color: isDefault ? '#1976d2' : undefined,
                              cursor: isAdminOrFacilityAdmin() ? 'pointer' : 'default',
                              textDecoration: isAdminOrFacilityAdmin() ? 'underline' : 'none'
                            }}
                            onClick={(event) => handleFacilityNameClick(facility.id, event)}
                          >
                            {facility.name}
                          </span>
                        </Tooltip>
                        {isDefault ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" sx={{ color: '#ffa726' }} />
                            <Typography variant="caption" sx={{ color: '#ffa726' }}>
                              Default
                            </Typography>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ minWidth: 0, color: '#1976d2', fontWeight: 400, textTransform: 'none' }}
                            disabled={saving}
                            onClick={() => handleSetDefaultFacility(facility.id)}
                          >
                            {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            Make this default
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {isAdmin() && pagination.totalPages > 1 && (
                <div className="facility-footer">
                  <Pagination
                    count={pagination.totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Showing {filteredFacilities.length} of {pagination.totalCount} facilities
                  </Typography>
                </div>
              )}
            </>
          ) : (
            <div className="facility-empty">
              No facilities found
            </div>
          )}
        </div>
      )}
      
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FacilityFilter;
